/* eslint-disable react/prop-types */
import {useContext, useEffect, useMemo, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import {useMount, useOnScreen} from '@s-ui/react-hooks'
import {useLocation, useRouter} from '@s-ui/react-router'
import ComparatorButton, {ComparatorDrawer, useComparator} from '@adv-mt/mt-comparator-button'
import EmptyCaseFavorites from '@adv-mt/mt-empty-case-favorites'
import Layout from '@adv-mt/mt-layout-basic'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import ListAds from '@adv-mt/mt-list-ads'
import {GridDealers} from '@adv-mt/mt-list-dealers'
import ListRelatedAds, {listRelatedAdsDisplay} from '@adv-mt/mt-list-related-ads'
import NavigationRequireAuth from '@adv-mt/mt-navigation-require-auth'
import NotificationsAdFavorite, {NOTIFICATIONS} from '@adv-mt/mt-notifications-ad-favorite'
import NotificationsAdsComparator from '@adv-mt/mt-notifications-ads-comparator'
import NotificationsDealerFavorite from '@adv-mt/mt-notifications-dealer-favorite'
import SearchSort, {searchSortButtonDesigns, searchSortPlaces} from '@adv-mt/mt-search-sort'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import LinkFactory from '../../../components/LinkFactory/index.js'
import Tabs, {TAB_IDS} from './components/Tabs.js'
import {getInitialProps} from './getInitialProps.js'

const PAGINATION_PLACEHOLDER = '%{pageNumber}'

const ComponentRecommender = ({recommendedAds, metadata, extendedTrackingData}) => {
  const {domain, i18n} = useContext(Context)
  const {track} = useAnalyticsTrack()

  const config = domain.get('config')
  const {TRACKING_EVENT_NAMES, TRACKING_EVENT_CONTEXTS} = config.get('tracking')
  const isMobile = config.get('isMobile')

  const isHigherThanMobileResolution = useMediaQuery(useMediaQueryBreakpoints.sm, {
    defaultMatches: !isMobile
  })

  const [isRelatedAdsCarouselOnScreen, relatedAdsCarouselRef] = useOnScreen()

  const {RECOMMENDED_ADS} = config.get('COMMON')
  const {maxRecommendations} = RECOMMENDED_ADS.userFavorites

  const handleClickRelatedAd = async ({ad, position}) => {
    const trackingData = await domain.get('get_event_tracking_data_use_case').execute({
      event: TRACKING_EVENT_NAMES.RECOMMENDATION_WIDGET_CLICKED,
      context: TRACKING_EVENT_CONTEXTS.FAVORITES_AD_DELETED,
      ads: [{ad, position}],
      recommendation: metadata
    })
    track(trackingData)
  }

  useEffect(() => {
    if (isRelatedAdsCarouselOnScreen) {
      domain
        .get('get_event_tracking_data_use_case')
        .execute({
          event: TRACKING_EVENT_NAMES.RECOMMENDATION_WIDGET_VIEWABLE_IMPRESSION,
          context: TRACKING_EVENT_CONTEXTS.FAVORITES_LIST,
          ads: recommendedAds,
          recommendation: metadata
        })
        .then(trackingData => {
          track(trackingData)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRelatedAdsCarouselOnScreen])

  return (
    <div ref={relatedAdsCarouselRef}>
      <ListRelatedAds
        ads={recommendedAds}
        title={i18n.t('COMMON.RELATED_FROM_AD_VISUALIZATION_IN_FAVOURITES')}
        display={listRelatedAdsDisplay.slides}
        numberOfSlides={isHigherThanMobileResolution ? 4 : 2}
        maxItems={maxRecommendations}
        itemsPerRow={maxRecommendations}
        extendedTrackingData={extendedTrackingData}
        onClickRelatedAd={handleClickRelatedAd}
      />
    </div>
  )
}

const UserFavorites = ({
  ads,
  totalAds,
  dealers,
  totalDealers,
  helmet,
  pageName,
  pageTrackingData,
  paginationPlaceholderUrl,
  search,
  showSearchSort,
  unsubscribeFavorite,
  section
}) => {
  const {domain, i18n} = useContext(Context)
  const {location, push} = useRouter()
  const {pathname} = useLocation()
  const {page, track} = useAnalyticsTrack()

  const [recommendedAds, setRecommendedAds] = useState([])
  const [recommendedAdsMetadata, setRecommendedAdsMetadata] = useState()
  const [recommendedAdsProducts, setRecomendedAdsProducts] = useState()

  const [isComparatorOpen, setIsComparatorOpen] = useState(false)

  const [currentAds, setCurrentAds] = useState(ads)
  const [currentDealers, setCurrentDealers] = useState(dealers)
  const [currentTotalAds, setCurrentTotalAds] = useState(totalAds)
  const [currentTotalDealers, setCurrentTotalDealers] = useState(totalDealers)

  // Determine active tab from URL section parameter
  const selectedTabId = section === 'dealers' ? TAB_IDS[1] : TAB_IDS[0]

  useEffect(() => {
    // Sync current ads with ads coming from initial props
    if (ads !== currentAds) {
      setCurrentAds(ads)
      setCurrentTotalAds(totalAds)
    }

    // Sync current dealers with dealers coming from initial props
    if (dealers !== currentDealers) {
      setCurrentDealers(dealers)
      setCurrentTotalDealers(totalDealers)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ads, totalAds, dealers, totalDealers])

  const config = domain.get('config')

  const {HAS_RECOMMENDER: isRecommenderActive} = config.get('search')
  const {SORT_FAVORITES} = config.get('favorites')

  const isMobile = config.get('isMobile')
  const isHigherThanMobile = useMediaQuery(useMediaQueryBreakpoints.sm, {
    defaultMatches: !isMobile
  })
  const isDesktop = useMediaQuery(useMediaQueryBreakpoints.lg, {
    defaultMatches: !isMobile
  })

  const {culture} = i18n

  const {RECOMMENDED_ADS, LISTING_SECTION_IDS} = config.get('COMMON')
  const recommendedAdsConfig = RECOMMENDED_ADS.userFavorites
  const {maxRecommendations, minRecommendations, minResultsToShowRecommendations} = recommendedAdsConfig

  const {USER_FAVORITE_RECOMMENDED_ADS_CAROUSEL_LIST_TYPE: recommendedAdsCarouselListType} =
    config.get('tracking').TRACKING_TRANSLATIONS_BY_CULTURE[culture]

  const {TRACKING_EVENT_CONTEXTS, TRACKING_EVENT_NAMES, HIT_ORIGINS} = config.get('tracking')

  // Comparator hook
  const {
    comparatorSelectedAds,
    comparatorNotificationType,
    handleSelectComparar,
    onRemoveSelectedAd,
    clearSelectedAds,
    resetNotification
  } = useComparator({
    pageName,
    origin: HIT_ORIGINS?.FAVORITES_PAGE,
    extendedTrackingData: pageTrackingData.data
  })

  const showRemoveSuccessfullyNotification = unsubscribeFavorite === 'true' || location?.state?.unsubscribeFavorite

  const hasMinTotalResultsToShowRecommendedAds = currentTotalAds >= minResultsToShowRecommendations
  const hasRecommendedAds = Boolean(recommendedAds.length)
  // Save favorited id so when we sort by elements the recommended ads won't change
  const lastFavoriteId = useMemo(() => {
    return currentAds?.[0]?.id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Calculate the position of the last favorited ad based on responsive grid layout
  const recommenderPosition = useMemo(() => {
    if (!hasRecommendedAds) return 0

    const lastFavoritedIdPosition = ads?.findIndex(item => {
      return item?.id === lastFavoriteId
    })

    const desktopOrTabledPosition = isDesktop ? 3 : 2
    const columns = isHigherThanMobile ? desktopOrTabledPosition : 1

    const currentRow = Math.floor(lastFavoritedIdPosition / columns)
    const endOfRowPosition = (currentRow + 1) * columns
    return Math.min(endOfRowPosition, currentAds.length)
  }, [hasRecommendedAds, ads, isDesktop, isHigherThanMobile, lastFavoriteId, currentAds.length])

  useEffect(() => {
    page(pageTrackingData)
  }, [page, pageTrackingData])

  useMount(() => {
    const {unsubscribe} = domain.get('get_url_params_search_use_case').subscribe(({params, result}) => {
      const [, urlParams] = result
      push({
        pathname: urlParams.path,
        search: urlParams.query,
        state: {
          lastFilter: params.lastFilter
        }
      })
    })
    return unsubscribe
  })

  useEffect(() => {
    domain.get('save_navigation_data_ads_use_case').execute({
      ads: currentAds,
      currentPage: 1,
      placeholder: PAGINATION_PLACEHOLDER,
      placeholderUrl: paginationPlaceholderUrl,
      searchUrl: pathname,
      totalPages: 1,
      totalResults: currentTotalAds
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAds])

  const trackRecommendedAds = ({ads}) =>
    domain
      .get('get_advertisements_tracking_use_case')
      .execute({
        ads: ads.map(({id, title, make, model, location, pack}) => ({
          id,
          title,
          make,
          model,
          location,
          pack
        })),
        adsTypeName: 'ad_impression',
        listType: recommendedAdsCarouselListType
      })
      .then(({products}) => {
        setRecomendedAdsProducts({products})
        track({
          event: 'Ad Impressions Collected',
          data: {
            products
          }
        })
      })

  const getRecommendedAds = async ({adId, maxItems, context}) => {
    const [recommendedAdsError, recommendedAdsResponse] = await domain
      .get('get_recommended_ads_use_case')
      .execute({adId, maxItems})

    if (recommendedAdsError) return

    const {items, metadata, isRecommendedAdsFallback} = recommendedAdsResponse

    if (items.length >= minRecommendations) {
      if (isRecommendedAdsFallback) {
        track({
          event: 'Recommended Ads Fallback',
          data: {
            hit_origin: pageName,
            hit_information: 'true'
          }
        })
      }
      trackRecommendedAds({ads: items})
      setRecommendedAds(items)
      setRecommendedAdsMetadata(metadata)
      const trackingData = await domain.get('get_event_tracking_data_use_case').execute({
        event: TRACKING_EVENT_NAMES.RECOMMENDATION_WIDGET_IMPRESSION_EVENT,
        context,
        ads: items,
        recommendation: metadata
      })
      track(trackingData)
    }
  }

  useEffect(() => {
    if (isRecommenderActive && hasMinTotalResultsToShowRecommendedAds) {
      getRecommendedAds({
        adId: lastFavoriteId,
        maxItems: maxRecommendations,
        context: TRACKING_EVENT_CONTEXTS.FAVORITES_LIST
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMinTotalResultsToShowRecommendedAds])

  const COMPARATOR_NOTIFICATION_CLASS = 'mt-UserFavorites-comparatorNotification'
  const hasAdsInComparatorMobile = comparatorSelectedAds?.length > 0 && !isHigherThanMobile
  const comparatorNotificationClassName = cx(COMPARATOR_NOTIFICATION_CLASS, {
    [`${COMPARATOR_NOTIFICATION_CLASS}--withBottomMargin`]: hasAdsInComparatorMobile && !isComparatorOpen,
    [`${COMPARATOR_NOTIFICATION_CLASS}--top`]: hasAdsInComparatorMobile && isComparatorOpen
  })

  return (
    <NavigationRequireAuth currentPath={pathname}>
      <Layout.Basic linkFactory={LinkFactory} isPaddingless>
        <Helmet {...helmet} />
        <main className="mt-UserFavorites">
          <section className="mt-UserFavorites-content">
            <TitleBasic size={titleBasicSizes.m} title={i18n.t('FAVORITE_PAGE.TITLE')} />
            <Tabs selectedTabId={selectedTabId} totalAds={currentTotalAds} totalDealers={currentTotalDealers} />
            {selectedTabId === TAB_IDS[0] && (
              <div className="mt-UserFavorites-section">
                {currentTotalAds > 0 && (
                  <div className="mt-UserFavorites-header">
                    {isHigherThanMobile && (
                      <TitleBasic
                        as={titleBasicTags.h2}
                        size={titleBasicSizes.xs}
                        title={`${currentTotalAds} ${i18n.t('FAVORITE_PAGE.SECTIONS.FAVORITE_ADS.TITLE')}`}
                      />
                    )}
                    {showSearchSort && (
                      <SearchSort
                        design={searchSortButtonDesigns.BASIC}
                        linkFactory={LinkFactory}
                        pageName={pageName}
                        place={searchSortPlaces.FAVORITE_LIST}
                        listingSectionId={LISTING_SECTION_IDS.FAVORITES_VEHICLES}
                        search={search}
                        sortConfigProps={SORT_FAVORITES}
                        withSortOrderInfo
                      />
                    )}
                  </div>
                )}
                <ListAds
                  ads={currentAds}
                  count={currentTotalAds}
                  isFavoritesList
                  showPriceDrop
                  pageName={pageName}
                  emptyCase={<EmptyCaseFavorites section={selectedTabId} />}
                  linkFactory={LinkFactory}
                  withAdViewedTracking
                  extendedTrackingData={pageTrackingData.data}
                  onAdsChanged={updatedAds => {
                    setCurrentAds(updatedAds)
                    setCurrentTotalAds(updatedAds.length)
                  }}
                  adSpaces={
                    hasRecommendedAds
                      ? [
                          {
                            position: recommenderPosition - 1,
                            component: (
                              <div className="mt-UserFavorites-recommender">
                                <ComponentRecommender
                                  recommendedAds={recommendedAds}
                                  metadata={recommendedAdsMetadata}
                                  extendedTrackingData={recommendedAdsProducts}
                                />
                              </div>
                            )
                          }
                        ]
                      : []
                  }
                  enableComparator
                  comparatorSelectedAds={comparatorSelectedAds}
                  handleSelectComparar={handleSelectComparar}
                />

                {comparatorSelectedAds.length > 0 && (
                  <div className="mt-UserFavorites-comparatorButton">
                    <ComparatorButton
                      origin={HIT_ORIGINS?.FAVORITES_PAGE}
                      comparatorSelectedAds={comparatorSelectedAds}
                      isOpen={isComparatorOpen}
                      onToggle={setIsComparatorOpen}
                    />
                  </div>
                )}
              </div>
            )}

            {selectedTabId === TAB_IDS[1] && (
              <div className="mt-UserFavorites-section">
                {isHigherThanMobile && currentTotalDealers > 0 && (
                  <div className="mt-UserFavorites-header">
                    <TitleBasic
                      as={titleBasicTags.h2}
                      size={titleBasicSizes.xs}
                      title={`${currentTotalDealers} ${i18n.t('FAVORITE_PAGE.SECTIONS.FAVORITE_DEALERS.TITLE')}`}
                    />
                  </div>
                )}
                <GridDealers
                  dealers={currentDealers}
                  count={currentTotalDealers}
                  emptyCase={<EmptyCaseFavorites section={selectedTabId} />}
                  pageName={pageName}
                  onDealersChanged={updatedDealers => {
                    setCurrentDealers(updatedDealers)
                    setCurrentTotalDealers(updatedDealers.length)
                  }}
                />
              </div>
            )}
          </section>

          <ComparatorDrawer
            isOpen={isComparatorOpen}
            onClose={() => setIsComparatorOpen(false)}
            comparatorSelectedAds={comparatorSelectedAds}
            onRemoveSelectedAd={onRemoveSelectedAd}
            clearSelectedAds={clearSelectedAds}
            extendedTrackingData={pageTrackingData.data}
            closeOnOutsideClick={!comparatorNotificationType}
          />
        </main>
        <NotificationsAdFavorite
          showNotification={showRemoveSuccessfullyNotification ? NOTIFICATIONS.SUCCESS_DELETE : NOTIFICATIONS.EMPTY}
        />
        <NotificationsDealerFavorite />
        <NotificationsAdsComparator
          type={comparatorNotificationType}
          onClose={resetNotification}
          comparatorSelectedAds={comparatorSelectedAds}
          extendedTrackingData={pageTrackingData.data}
          className={comparatorNotificationClassName}
        />
      </Layout.Basic>
    </NavigationRequireAuth>
  )
}

UserFavorites.displayName = 'UserFavorites'

UserFavorites.propTypes = {
  ads: PropTypes.array,
  dealers: PropTypes.array,
  helmet: PropTypes.object,
  pageName: PropTypes.string,
  pageTrackingData: PropTypes.object,
  paginationPlaceholderUrl: PropTypes.string,
  search: PropTypes.object,
  section: PropTypes.string,
  showSearchSort: PropTypes.bool,
  totalAds: PropTypes.number,
  totalDealers: PropTypes.number,
  unsubscribeFavorite: PropTypes.string
}

UserFavorites.getInitialProps = getInitialProps

UserFavorites.keepMounted = true

export default UserFavorites
